import { useCallback, useMemo } from "react";

import { type ModalProps } from "@shapr3d/frontend-lib";

import type {
  SeatBasedTeamSubscription,
  TeamsSubscriptionsEstimateResponseData,
} from "../../../hooks/useTeamsTypes";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../../lib/TrackingAdapter";
import { SubscriptionPeriod } from "../../../types";
import {
  SubscriptionUpgradeModal,
  type SubscriptionUpgradeModalCopy,
} from "../SubscriptionUpgradeModal/SubscriptionUpgradeModal";

export const AddSeatsModal: React.FC<
  Pick<ModalProps, "open" | "onClose"> & {
    subscription: SeatBasedTeamSubscription;
    teamID: string | undefined;
  }
> = ({ subscription, teamID, onClose, open }) => {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  const copy: SubscriptionUpgradeModalCopy = useMemo(
    () => ({
      title: t(m.AddSeatsModal.Title),
      submitButton: subscription.isTrial
        ? t(m.AddSeatsModal.SubmitButtonTrial)
        : t(m.AddSeatsModal.SubmitButton),
      toastSuccess: (seats) => t(m.AddSeatsModal.ToastSuccess, { count: seats ?? 1 }),
      errorTitle: t(m.AddSeatsModal.ErrorTitle),
      errorText: t(m.AddSeatsModal.ErrorText),
      paymentErrorTitle: t(m.AddSeatsModal.PaymentErrorTitle),
      paymentErrorText: t(m.AddSeatsModal.PaymentErrorText),
      paymentCTAText: t(m.AddSeatsModal.PaymentCTAText),
      tertiaryText: ({
        currencySymbol,
        proratedTotalAmount,
        renewalCurrencyCode,
        proratedCurrencyCode,
      }) =>
        t(m.AddSeatsModal.TertiaryText, {
          currencySymbol,
          amount: proratedTotalAmount,
          allPricesInUSD:
            renewalCurrencyCode === "USD"
              ? t(m.Checkout.Summary.AllPricesIn, { currency: proratedCurrencyCode })
              : "",
        }),
      learnMore: t(m.AddSeatsModal.LearnMoreAboutTeams),
    }),
    [subscription.isTrial, t, m]
  );

  const onEstimateData = useCallback(
    (estimateData: TeamsSubscriptionsEstimateResponseData | undefined, isOpen: boolean) => {
      if (isOpen) {
        const priceShownConfig: Parameters<typeof tracker.trackPriceShown>[0] = {
          currencyCode: estimateData?.renewal.currencyCode ?? "USD",
          ...(estimateData?.renewal.period === SubscriptionPeriod.Yearly
            ? { yearlyPrice: Number(estimateData?.renewal.unitAmount ?? 0) }
            : { monthlyPrice: Number(estimateData?.renewal.unitAmount ?? 0) }),
        };
        tracker.trackPriceShown(priceShownConfig);
      }
    },
    [tracker]
  );

  return (
    <SubscriptionUpgradeModal
      subscription={subscription}
      teamID={teamID}
      onClose={onClose}
      open={open}
      defaultAdditionalSeats={1}
      copy={copy}
      onEstimateData={onEstimateData}
      onSeatCountChange={(count) => {
        tracker.trackButtonClick({
          action: ElementClickAction.SEAT_COUNT_CHANGE,
          location: ElementClickLocation.ADD_SEATS_MODAL,
          details: `${count}`,
        });
      }}
      onLearnMoreClick={() => {
        tracker.trackButtonClick({
          action: ElementClickAction.GO_TO_LEARN_MORE,
          location: ElementClickLocation.ADD_SEATS_MODAL,
        });
      }}
      onSubmitClick={(seats) => {
        tracker.trackAddSeatsModalConfirmed({
          seats: seats ?? 1,
          trial: subscription.isTrial,
        });
      }}
    />
  );
};
